import { Map as MapLibreMap } from "maplibre-gl"

// Animation timing for pulsing effect
const PULSE_DURATION = 2000 // Change this: smaller = faster pulse
const PULSE_SCALE = 1 // Change this: how much the ring expands (0.5 = 50% larger)
const PULSE_OPACITY = 0.8 // Change this: maximum opacity of the pulse ring
const DOT_RADIUS = 50 // Size of the dot

const CANVAS_SIZE = DOT_RADIUS + DOT_RADIUS * PULSE_SCALE // Account for pulse expansion

export type PulsingDotLayer = {
  width: number
  height: number
  data: Uint8ClampedArray
  context: CanvasRenderingContext2D
  onAdd: () => void
  render: () => boolean
}

// Class used to create a location dot on the map

const PulsingDot = (map: MapLibreMap): PulsingDotLayer => {
  const size = CANVAS_SIZE

  return {
    width: size,
    height: size,
    data: new Uint8ClampedArray(size * size * 4),
    context: null as unknown as CanvasRenderingContext2D,

    onAdd() {
      const canvas = document.createElement("canvas")
      canvas.width = this.width
      canvas.height = this.height
      this.context = canvas.getContext("2d") as CanvasRenderingContext2D
    },

    render() {
      const locationSource = map.getSource("location-point") as any
      let currentBearing: number | undefined = undefined
      let currentActivity: string | undefined = undefined

      if (
        locationSource &&
        locationSource._data &&
        locationSource._data.features &&
        locationSource._data.features.length > 0
      ) {
        const feature = locationSource._data.features[0]
        currentBearing = feature.properties?.bearing
        currentActivity = feature.properties?.activity
      }

      const context = this.context
      const centerX = size / 2
      const centerY = size / 2
      const radius = (DOT_RADIUS / 2) * 0.7

      const isDriving = currentActivity === "driving"
      const t = (performance.now() % PULSE_DURATION) / PULSE_DURATION
      const pulseIntensity = (Math.sin(t * Math.PI * 2) + 1) / 2 // 0 to 1

      // Clear canvas
      context.clearRect(0, 0, this.width, this.height)

      // Draw pulsing gray ring only when driving
      if (isDriving) {
        const outerRadius = radius + pulseIntensity * radius * PULSE_SCALE
        const outerOpacity = PULSE_OPACITY * (1 - pulseIntensity)

        context.beginPath()
        context.arc(centerX, centerY, outerRadius, 0, Math.PI * 2)
        context.fillStyle = `rgba(128, 128, 128, ${outerOpacity})` // Gray color
        context.fill()
      }

      // Draw main circle (green when driving, orange when not)
      const dotColor = isDriving ? "#4CAF50" : "#F59E0B" // Green or amber/orange

      context.beginPath()
      context.arc(centerX, centerY, radius, 0, Math.PI * 2)
      context.fillStyle = dotColor
      context.fill()

      // Draw white border (always present)
      context.beginPath()
      context.arc(centerX, centerY, radius, 0, Math.PI * 2)
      context.strokeStyle = "white"
      context.lineWidth = 4
      context.stroke()

      // Draw chevrons only when driving with bearing
      if (currentBearing !== undefined && isDriving) {
        context.save()
        context.translate(centerX, centerY)
        context.rotate((currentBearing * Math.PI) / 180)

        const chevronSize = radius * 0.8
        const chevronThickness = 3

        context.strokeStyle = "white"
        context.lineWidth = chevronThickness
        context.lineCap = "round"
        context.lineJoin = "round"

        // First chevron (closer to top)
        context.beginPath()
        context.moveTo(-chevronSize / 2, -chevronSize / 4)
        context.lineTo(0, -chevronSize / 2)
        context.lineTo(chevronSize / 2, -chevronSize / 4)
        context.stroke()

        // Second chevron (below first one)
        context.beginPath()
        context.moveTo(-chevronSize / 2, chevronSize / 4)
        context.lineTo(0, 0)
        context.lineTo(chevronSize / 2, chevronSize / 4)
        context.stroke()

        context.restore()
      }

      // Update image data
      this.data = context.getImageData(0, 0, this.width, this.height).data

      // Trigger map repaint
      map.triggerRepaint()

      return true
    },
  }
}

export default PulsingDot
